import { useEffect, useMemo, useState } from 'react'

import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table'

import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CurrencyPaymentItem } from '@/interfaces/billing'

import { getColumns } from './Columns'
import { HeaderActions } from './HeaderActions'
import { Navigation } from './Navigation'
import { useTableContext } from './TableContext'
import { useNavigate } from 'react-router-dom'

// DOCS: https://ui.shadcn.com/docs/components/data-table#basic-table

export function DataTable() {
  const {
    currentPage,
    currencyPaymentsData,
    isLoading,
    skeletonRowCount,
    isFetching,
    isError,
  } = useTableContext()

  const [isMobileView, setIsMobileView] = useState(false)

  const columns = useMemo(() => getColumns(), [])

  const skeletonColumns = useMemo(() => {
    if (!isLoading) return columns
    return columns.map((column) => {
      const width = '80%'
      const height = '20px'
      return {
        ...column,
        cell: () => (
          <div className="flex items-center">
            <Skeleton className={`h-${height} w-${width}`} />
          </div>
        ),
      }
    })
  }, [columns, isLoading])

  const skeletonData = useMemo(() => {
    if (!isLoading) return []
    return Array.from({ length: skeletonRowCount }).map((_, index) => ({
      id: `skeleton-${index}`,
    }))
  }, [isLoading, skeletonRowCount])

  const tableData = useMemo(() => {
    if (isLoading) {
      return skeletonData as unknown as CurrencyPaymentItem[]
    }
    return (currencyPaymentsData?.items as CurrencyPaymentItem[]) || []
  }, [currencyPaymentsData?.items, isLoading, skeletonData])

  const navigate = useNavigate()

  const navigateToInvoice = (
    row: Row<CurrencyPaymentItem>,
    event: React.MouseEvent
  ) => {
    // Don't navigate if in loading state
    if (isLoading) return

    const invoice = row.original.orderReceipt

    event.stopPropagation()

    // Build the invoice URL - navigate to clean details URL
    // The localStorage will handle filter persistence
    const invoiceUrl = `/registers-beta/all/${invoice.id}-${
      invoice.operationNumber != null
        ? invoice.operationNumber
        : invoice.preOperationNumber
    }`

    navigate(invoiceUrl)
  }

  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }
    checkViewport()
    window.addEventListener('resize', checkViewport)
    return () => {
      window.removeEventListener('resize', checkViewport)
    }
  }, [])

  const table = useReactTable({
    data: tableData,
    columns: isLoading
      ? (skeletonColumns as ColumnDef<CurrencyPaymentItem, unknown>[])
      : columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  const renderMobileCard = (row: Row<CurrencyPaymentItem>) => {
    if (isLoading) {
      return (
        <div
          aria-rowindex={row.index + 2}
          className="mb-4 rounded-md border bg-card p-4 shadow-sm"
          key={row.id}
          role="row"
        >
          <div className="grid grid-cols-2 gap-2">
            {table.getVisibleLeafColumns().map((column) => (
              <div className="mb-2" key={column.id} role="cell">
                <div className="text-xs font-medium text-muted-foreground">
                  {typeof column.columnDef.header === 'string'
                    ? column.columnDef.header
                    : column.id}
                </div>
                <div className="flex h-5 items-center">
                  <Skeleton className="h-4 w-4/5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }
    return (
      <div
        aria-rowindex={row.index + 2}
        className="mb-4 cursor-pointer rounded-md border bg-card p-4 shadow-sm hover:bg-accent/50"
        key={row.id}
        onClick={(e) => navigateToInvoice(row, e)}
        role="row"
      >
        <div className="grid grid-cols-2 gap-2">
          {row.getVisibleCells().map((cell) => {
            if (cell.column.id === 'shipping') return null
            const header =
              typeof cell.column.columnDef.header === 'string'
                ? cell.column.columnDef.header
                : cell.column.id
            return (
              <div className="mb-2" key={cell.id} role="cell">
                <div className="text-xs font-medium text-muted-foreground">
                  {header}
                </div>
                <div>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card">
      <HeaderActions
        columns={
          table.getAllColumns() as unknown as Column<
            CurrencyPaymentItem,
            unknown
          >[]
        }
      />
      <div className="border-t">
        <div>
          {isLoading ||
          isFetching ||
          isError ||
          table.getRowModel().rows?.length ? (
            isMobileView ? (
              <div
                aria-label="Tabla de pagos recibidos"
                className="p-4"
                role="table"
              >
                <div className="sr-only" role="rowgroup">
                  <div role="row">
                    {table.getHeaderGroups().map((headerGroup) =>
                      headerGroup.headers.map((header) => (
                        <div key={header.id} role="columnheader">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div key={`page-${currentPage}`} role="rowgroup">
                  {table.getRowModel().rows.map((row) => renderMobileCard(row))}
                </div>
              </div>
            ) : (
              <div
                aria-label="Tabla de pagos recibidos"
                className="overflow-x-auto"
                role="region"
              >
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead className="px-4 text-left" key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody key={`page-${currentPage}`}>
                    {table.getRowModel().rows.map((row) => {
                      if (isLoading) {
                        return (
                          <TableRow className="h-11 text-left" key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <TableCell
                                className="px-0.5 py-1 text-left"
                                key={cell.id}
                              >
                                <Skeleton className="h-10 w-full rounded-none bg-gray-100" />
                              </TableCell>
                            ))}
                          </TableRow>
                        )
                      }
                      return (
                        <TableRow
                          className="cursor-pointer hover:bg-accent/50"
                          data-state={row.getIsSelected() && 'selected'}
                          key={row.id}
                          onClick={(e) => navigateToInvoice(row, e)}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              className="px-4 py-1 text-left"
                              key={cell.id}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <div className="py-10 text-center">
              <p className="text-muted-foreground">
                {isError ? 'Error al cargar los datos' : 'No hay resultados'}
              </p>
            </div>
          )}
          <Navigation />
        </div>
      </div>
    </div>
  )
}
